import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LIGHTBOX_Z_INDEX } from '../../hooks/useFloatingPanelPosition';
import { useLanguage } from '../../contexts/LanguageContext';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
  );
}

function ImageLightbox({
  isOpen,
  onClose,
  src,
  alt = '',
  title,
  children,
}) {
  const { t } = useLanguage();
  const closeLabel = t.common?.close || 'Close';
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const focusDialog = () => {
      const node = dialogRef.current;
      if (!node) return;
      const focusables = getFocusableElements(node);
      const target = focusables[0] || node;
      target.focus?.();
    };

    const frame = requestAnimationFrame(focusDialog);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const node = dialogRef.current;
      if (!node) return;
      const focusables = getFocusableElements(node);
      if (focusables.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown, true);
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === 'function') {
        previouslyFocusedRef.current = null;
        previous.focus();
      }
    };
  }, [isOpen, onClose]);

  if (typeof document === 'undefined') return null;

  const content = (
    <AnimatePresence>
      {isOpen ? (
        <div
          ref={dialogRef}
          className="fixed inset-0 flex items-center justify-center safe-x p-3 sm:p-6"
          style={{ zIndex: LIGHTBOX_Z_INDEX }}
          tabIndex={-1}
        >
          <motion.button
            type="button"
            aria-label={closeLabel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-secondary-950/85 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title || alt}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            className="relative z-10 w-full max-w-[min(92vw,56rem)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="absolute right-2 top-2 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-secondary-200/80 bg-white text-secondary-600 shadow-lg transition-colors hover:bg-secondary-50 hover:text-secondary-900      sm:-right-3 sm:-top-3"
            >
              <XMarkIcon className="h-5 w-5 shrink-0" aria-hidden />
            </button>

            {title ? (
              <p className="mb-3 text-center text-sm font-medium text-white/90 sm:text-base">
                {title}
              </p>
            ) : null}

            {src ? (
              <img
                src={src}
                alt={alt}
                className="mx-auto max-h-[min(85dvh,48rem)] w-full rounded-2xl object-contain shadow-premium"
              />
            ) : (
              children
            )}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}

export default ImageLightbox;
